import { useMemo, useState } from "react";
import { Slider } from "@/components/ui/slider";
import { WaitlistDialog } from "@/components/WaitlistDialog";
import { useI18n } from "@/lib/i18n";

export function RoiSavings() {
  const { t } = useI18n();
  const [fee, setFee] = useState(2000);
  const [spend, setSpend] = useState(1500);

  const { savePerMonth, savePerYear, pctBack } = useMemo(() => {
    const adkavi = 97;
    const savePerMonth = Math.max(0, fee - adkavi);
    const savePerYear = savePerMonth * 12;
    const pctBack = fee > 0 ? Math.round((savePerMonth / fee) * 100) : 0;
    return { savePerMonth, savePerYear, pctBack };
  }, [fee]);

  const fmt = (n: number) => `$${n.toLocaleString()}`;

  return (
    <section className="py-24 px-6">
      <div className="max-w-4xl mx-auto">
        <div className="text-center mb-10">
          <h2 className="text-3xl md:text-4xl font-bold tracking-tight mb-3">
            {t("roi.title")} <span className="text-gradient-orange">{t("roi.titleHighlight")}</span>
          </h2>
          <p className="text-muted-foreground">
            {t("roi.desc")}
          </p>
        </div>

        <div className="grid md:grid-cols-2 gap-5 mb-6">
          <div className="rounded-2xl bg-card border border-border p-6">
            <div className="flex items-baseline justify-between mb-4">
              <label className="text-sm font-medium">{t("roi.feeLabel")}</label>
              <span className="text-lg font-bold text-primary">{fmt(fee)}</span>
            </div>
            <Slider
              value={[fee]}
              min={0}
              max={10000}
              step={50}
              onValueChange={(v) => setFee(v[0])}
            />
            <div className="flex justify-between text-xs text-muted-foreground mt-2">
              <span>$0</span><span>$10,000</span>
            </div>
          </div>

          <div className="rounded-2xl bg-card border border-border p-6">
            <div className="flex items-baseline justify-between mb-4">
              <label className="text-sm font-medium">{t("roi.spendLabel")}</label>
              <span className="text-lg font-bold text-primary">{fmt(spend)}</span>
            </div>
            <Slider
              value={[spend]}
              min={100}
              max={50000}
              step={100}
              onValueChange={(v) => setSpend(v[0])}
            />
            <div className="flex justify-between text-xs text-muted-foreground mt-2">
              <span>$100</span><span>$50,000</span>
            </div>
          </div>
        </div>

        <div className="rounded-3xl bg-card border-2 border-primary p-8 text-center">
          <p className="text-muted-foreground mb-6">
            {t("roi.spending", { amount: `${fmt(fee)}` })}
          </p>

          <p className="text-sm uppercase tracking-wider text-muted-foreground mb-2">
            {t("roi.withAdkavi")}
          </p>
          <div className="flex flex-col md:flex-row items-center justify-center gap-4 md:gap-10 mb-4">
            <div>
              <div className="text-4xl md:text-5xl font-bold text-primary">{fmt(savePerMonth)}</div>
              <div className="text-xs text-muted-foreground uppercase tracking-wider mt-1">{t("roi.perMonth")}</div>
            </div>
            <div className="hidden md:block w-px h-12 bg-border" />
            <div>
              <div className="text-4xl md:text-5xl font-bold text-primary">{fmt(savePerYear)}</div>
              <div className="text-xs text-muted-foreground uppercase tracking-wider mt-1">{t("roi.perYear")}</div>
            </div>
          </div>

          <p className="text-lg mb-6">
            {t("roi.pocketPre")} <span className="text-primary font-bold text-2xl">{pctBack}%</span> {t("roi.pocketSuf")}
          </p>

          <WaitlistDialog>
            <button className="bg-orange-500 hover:bg-orange-600 text-white transition rounded-full px-8 py-3 text-sm font-semibold">
              {t("roi.cta")}
            </button>
          </WaitlistDialog>
        </div>
      </div>
    </section>
  );
}
